/**
 * Cart Types
 *
 * Type definitions for cart functionality.
 * These types mirror the backend DTOs from CustomerCartController.
 */

// =============================================================================
// Pricing Option Enums
// =============================================================================

/**
 * Type of pricing option selected by customer
 */
export enum PricingOptionType {
    Rental = 0,
    MonthlySubscription = 1,
    RentToBuy = 2, // Future
    Buy = 3, // Future
}

/**
 * Pricing tier for short-term rentals
 */
export enum RentalPricingTier {
    Default = 0, // System calculates from duration
    Day1 = 1,
    Days2To3 = 2,
    Days4To7 = 3,
    Days8To14 = 4,
    Days15To21 = 5,
    Days22To40 = 6,
}

/**
 * Subscription commitment duration
 * 0 = Month-to-month (no commitment)
 */
export enum SubscriptionDuration {
    MonthToMonth = 0,
    Months3 = 3,
    Months6 = 6,
    Months12 = 12,
}

// =============================================================================
// Cart Item Types
// =============================================================================

export interface CustomerCartItemResponse {
    id?: string;
    equipmentId?: string;
    equipmentName?: string | null;
    imageUrl?: string | null;
    locationId?: string | null;
    locationName?: string | null;
    quantity?: number;
    unitPrice?: number;
    startDate?: string;
    endDate?: string;
    totalPrice?: number;
    depositAmount?: number;
    pricingBreakdown?: ItemPricingBreakdown;

    // Pricing option fields
    pricingOptionType?: PricingOptionType;
    pricingOptionLabel?: string | null;
    selectedRentalTier?: RentalPricingTier | null;
    subscriptionMonths?: SubscriptionDuration | null;
    isSubscription?: boolean;

    // Per-item specificity fields
    isNewItem?: boolean;
    subscriptionMonthlyRate?: number | null;
}

// =============================================================================
// Cart Response Types
// =============================================================================

export interface CustomerCartResponse {
    sessionId?: string | null;
    itemCount?: number;
    items?: CustomerCartItemResponse[] | null;
    subtotal?: number;
    discountAmount?: number;
    discountCode?: string | null;
    taxAmount?: number;
    deliveryFee?: number;
    depositAmount?: number;
    totalAmount?: number;
    pricingBreakdown?: CartPricingBreakdown;
    createdAt?: string;
    updatedAt?: string;
}

// =============================================================================
// Cart Request Types
// =============================================================================

export interface CustomerAddToCartRequest {
    equipmentId: string;
    quantity: number;
    locationId: string;

    // Explicit pricing option
    pricingOptionType: PricingOptionType;

    // For Rental pricing
    startDate?: string;
    endDate?: string;
    /** When true, the customer explicitly chose both dates (fixed-dates flow). */
    endDateIsExact?: boolean;
    selectedRentalTier?: RentalPricingTier;

    // For Subscription pricing
    subscriptionMonths?: SubscriptionDuration;
    subscriptionStartDate?: string;

    // New item premium pricing
    isNewItem?: boolean;

    /** @deprecated - Use pricingOptionType instead */
    unitPrice?: number;
    deliveryAddress?: string;
}

export interface UpdateCartItemRequest {
    quantity: number;
}

// =============================================================================
// Pricing Types
// =============================================================================

export interface PricingCalculationRequest {
    items: PricingCalculationItem[];
    locationId?: string;
    deliveryAddress?: string;
    deliveryFee?: number;
}

export interface PricingCalculationItem {
    equipmentTypeId: string;
    quantity: number;
    startDate: string;
    endDate: string;
    unitPrice: number;
}

export interface CartItemPricingRequest {
    equipmentTypeId: string;
    quantity: number;
    startDate: string;
    endDate: string;
    unitPrice: number;
}

export interface ItemPricingBreakdown {
    equipmentTypeId?: string;
    rentalDays?: number;
    isLongTerm?: boolean;
    basePrice?: number;
    discountAmount?: number;
    discountPercentage?: number;
    adjustedPrice?: number;
    depositAmount?: number;
    description?: string | null;
}

export interface CartPricingBreakdown {
    subtotal?: number;
    totalDeposit?: number;
    deliveryFee?: number;
    totalDiscount?: number;
    grandTotal?: number;
    itemBreakdowns?: ItemPricingBreakdown[] | null;
}

// =============================================================================
// Cart State Types (for Zustand store)
// =============================================================================

export interface CartItem {
    id: string;
    equipmentId: string;
    equipmentTypeId: string;
    productName: string;
    productImage?: string;
    slug?: string; // Product slug for SEO-friendly URLs
    locationId: string;
    locationName?: string;
    quantity: number;

    // Explicit Pricing Option
    pricingOptionType: PricingOptionType;
    pricingOptionLabel: string; // Human-readable: "Short-term Rental", "6-Month Subscription"

    // Rental fields (when pricingOptionType === Rental)
    startDate?: string;
    endDate?: string;
    /**
     * True when the customer explicitly selected both a start and end date (fixed-dates flow).
     * False/absent when the end date was derived from a tier bracket (tier+pickup flow).
     * Used by inferTierRange to decide whether to show an exact date or the full tier window.
     */
    endDateIsExact?: boolean;
    rentalDays?: number; // Calculated from startDate/endDate for rentals
    selectedRentalTier?: RentalPricingTier;
    unitPrice?: number;

    // Subscription fields (when pricingOptionType === MonthlySubscription)
    subscriptionMonths?: SubscriptionDuration; // 0 = month-to-month
    subscriptionMonthlyRate?: number;
    isNoCommitment?: boolean; // true when subscriptionMonths === 0
    monthlyRentalLifecycle?: {
        paidThroughReturnDate?: string;
        nextChargeDate?: string;
        commitmentEndDate?: string;
        cancellationReturnDeadline?: string;
        paymentGraceExpiresAt?: string;
    };

    // Calculated pricing
    totalPrice?: number;
    depositAmount?: number;
    pricing?: ItemPricingBreakdown;
    /**
     * Whether the backend could resolve a price for this line's dates/duration.
     * `false` (PRC-G2/#517) when no tier band applies: the line must NOT check out
     * at a stale local price — surface a request-a-quote affordance instead.
     * Absent or `true` means the line is priced normally.
     */
    isPriceAvailable?: boolean;

    // Display helpers
    dateDisplayText?: string; // Human-readable date/duration text

    // Accessory fields
    isAccessory?: boolean;

    // New item premium pricing
    isNewItem?: boolean;

    /** @deprecated - Use pricingOptionType === MonthlySubscription instead */
    isSubscription?: boolean;
}

// =============================================================================
// Admin Reservation Hydration Types
// =============================================================================

/**
 * Snapshot of cart items and context shelved when an admin reservation
 * overwrites the current cart. Restored by `clearReservation()` if present.
 */
export interface ShelvedCart {
    items: CartItem[];
    locationId: string | null;
}

/**
 * Contact info payload within a reservation hydration response.
 */
export interface ReservationContactPayload {
    firstName?: string | null;
    lastName?: string | null;
    email?: string | null;
    phone?: string | null;
}

/**
 * A single cart item in the reservation hydration payload.
 */
export interface ReservationCartItemPayload {
    equipmentTypeId: string;
    equipmentTypeName?: string | null;
    imageUrl?: string | null;
    quantity: number;
    unitPrice: number;
    totalPrice: number;
    /** 0 = Rental, 1 = MonthlySubscription */
    pricingOptionType: number;
    subscriptionMonths?: number | null;
}

/**
 * Open Graph metadata carried in the reservation hydration payload.
 * Used by ResumeReservationPage to set og:title / og:description.
 */
export interface ReservationOgPayload {
    /** Brand name, e.g. "Stork Rentals". */
    brand: string;
    /** Location display name. */
    locationName: string;
}

/**
 * Hydration payload returned by GET /api/customer/reservations/resume/{token}.
 * Mirrors the backend `ResumeReservationResponse` shape from FEAT-010.
 */
export interface ReservationHydrationPayload {
    reservationId: string;
    bookingNumber: string;
    locationId?: string | null;
    locationName: string;
    pickupDate: string;
    returnDate: string;
    contact: ReservationContactPayload;
    items: ReservationCartItemPayload[];
    lockedSubtotal: number;
    lockedDeposit: number;
    /** Amount due now, with appliedDiscountAmount already subtracted. */
    lockedTotal: number;
    appliedDiscountCode?: string | null;
    /**
     * Discount applied by appliedDiscountCode, recomputed server-side against this
     * cart (the same amount the charge applies). lockedTotal already has it subtracted.
     */
    appliedDiscountAmount?: number | null;
    /**
     * Recurring portion of appliedDiscountAmount — the discount applied to each monthly
     * subscription charge (not just the first). Lets the locked cart show the discounted
     * recurring monthly amount instead of the gross rate. 0 for one-time discounts.
     */
    appliedDiscountRecurringAmount?: number | null;
    adminNote?: string | null;
    expiresAt: string;
    /** Resume token from URL — caller passes this through. */
    resumeToken: string;
    /** Open Graph metadata for the resume page. */
    og: ReservationOgPayload;
}

/**
 * Cart State
 *
 * Note: Pickup-only fulfillment model - no delivery fields.
 * See: docs/customer-website/spec/spec-design-pickup-only-fulfillment.md
 */
export interface CartState {
    // Session
    sessionId: string | null;

    // Cart data
    items: CartItem[];
    locationId?: string;

    // Pricing (no deliveryFee - pickup only)
    subtotal: number;
    totalDeposit: number;
    totalDiscount: number;
    /**
     * Recurring portion of totalDiscount for a locked reservation — the per-cycle discount
     * on monthly subscription items. Used to display the discounted recurring monthly amount
     * when there is no live promoValidationResult (locked carts). 0 otherwise.
     */
    recurringDiscountAmount: number;
    grandTotal: number;
    /** VAT rate for the selected location (e.g. 21 for 21%). Undefined or 0 if no VAT configured. */
    vatRate?: number;
    /** VAT amount included in subtotal. Undefined or 0 if no VAT. */
    vatAmount?: number;
    calculatedAt?: string;

    // Admin reservation lock state
    /** True when the cart is driven by an admin-initiated reservation link. */
    isLocked: boolean;
    /** ID of the admin reservation currently driving the cart. Null when not locked. */
    reservationId: string | null;
    /** Resume token from the URL, persisted so checkout can send it to the backend. */
    resumeToken: string | null;
    /**
     * Previous cart snapshotted when an admin reservation hydration replaced it.
     * Restored by `clearReservation()`.
     */
    shelvedCart: ShelvedCart | null;
    /**
     * Display name of the location associated with the locked reservation.
     * Shown in the ReservationBanner. Null when not locked.
     */
    locationName: string | null;
    /**
     * Optional admin-authored note attached to the reservation.
     * Shown in AdminNoteCallout when present. Null when not locked or no note.
     */
    adminNote: string | null;

    // UI state
    isLoading: boolean;
    isDrawerOpen: boolean;
    error: string | null;
    /** Transient state: set when an item is added on mobile to show the banner instead of the drawer */
    lastAddedItem: { name: string; image?: string } | null;

    // Validation state
    isValidating: boolean;
    validationErrors: CartValidationError[];
}

export interface CartValidationError {
    itemId: string;
    equipmentName: string;
    error: string;
    availableQuantity?: number;
    requestedQuantity?: number;
    priceChanged?: boolean;
    newPrice?: number;
    isAccessory?: boolean;
}

// =============================================================================
// Cart Validation Types
// Note: Main validation types are now defined in api/cart.ts
// These are kept for backwards compatibility
// =============================================================================

// Re-export from api/cart for backwards compatibility
export type {
    CartValidationResult,
    CartItemValidation,
    CartAccessoryValidation,
    CartValidationTotals,
    CartValidationRequest,
} from '@/api/cart';

// =============================================================================
// Export legacy types for backwards compatibility
// =============================================================================

// Commenting out to avoid circular import issues
// export type { PricingRequest, PricingResponse } from '@/types';

// =============================================================================
// Type Guards and Helpers
// =============================================================================

/**
 * Check if cart item is a rental
 */
export function isRentalItem(item: CartItem): boolean {
    return item.pricingOptionType === PricingOptionType.Rental;
}

/**
 * Check if cart item is a subscription
 */
export function isSubscriptionItem(item: CartItem): boolean {
    return item.pricingOptionType === PricingOptionType.MonthlySubscription;
}

/**
 * Check if subscription is month-to-month (no commitment)
 */
export function isNoCommitmentSubscription(item: CartItem): boolean {
    return (
        isSubscriptionItem(item) &&
        item.subscriptionMonths === SubscriptionDuration.MonthToMonth
    );
}

/**
 * Get human-readable label for subscription duration
 */
export function getSubscriptionLabel(months: SubscriptionDuration | number): string {
    switch (months) {
        case SubscriptionDuration.MonthToMonth:
            return 'Flexible (Monthly)';
        case SubscriptionDuration.Months3:
            return '3-Month Plan';
        case SubscriptionDuration.Months6:
            return '6-Month Plan';
        case SubscriptionDuration.Months12:
            return '12-Month Plan';
        default:
            return `${months}-Month Plan`;
    }
}

/**
 * Get human-readable label for rental tier
 */
export function getRentalTierLabel(tier: RentalPricingTier): string {
    switch (tier) {
        case RentalPricingTier.Day1:
            return '1 Day';
        case RentalPricingTier.Days2To3:
            return '2-3 Days';
        case RentalPricingTier.Days4To7:
            return '4-7 Days';
        case RentalPricingTier.Days8To14:
            return '8-14 Days';
        case RentalPricingTier.Days15To21:
            return '15-21 Days';
        case RentalPricingTier.Days22To40:
            return '22-40 Days';
        default:
            return 'Standard Rental';
    }
}
